import emoji from "emoji-dictionary"
import { fs } from "../controls/Constants"
import CodeFormatter from "../components/CodeFormatter"
import CodeArticle from "../components/CodeArticle"

const KariPIR = () => {
    const usage = 
    `#include <kari.h>

using namespace kari;

kariPIR *pirSensor;

void motionDetected() {
    Serial.println("Motion detected!");
}

void noMotion() {
    Serial.println("No motion detected.");
}

void setup() {
    Serial.begin(9600);
    kariBegin({8});
    pirSensor = new kariPIR(8);  // Initialize PIR sensor on pin 8
}

void loop() {
    kariAsync::execute(
    [] () {
        pirSensor->onMeasure(motionDetected, noMotion);  // Detect motion continuously
    },
    100
    );
}
    `
    const codeSnippet = 
    `#include <kari.h>
using namespace kari;

kariPIR *pir;
void setup(){
    kariBegin({7});
    pir = new kariPIR(8);
}


void loop(){
    kariAsync::execute(
        [](){
            pir->onMeasure(
                [](){ digitalWrite(7, HIGH); },
                [](){ digitalWrite(7, LOW); }
            );

        },10
    );
}
    `
    const introduction = 
    `The kariPIR class is a pivotal component of the Kari Arduino Library, designed to bring precision and efficiency to motion detection in your Arduino projects. 
    As a solution tailored for interfacing with Passive Infrared (PIR) sensors, kariPIR enables seamless and reliable motion-sensing capabilities, 
    making it an indispensable tool for developers working on a variety of applications, including security systems, home automation, and interactive installations.\n
    Motion detection is a crucial feature in many embedded systems, requiring both accuracy and timely responses. 
    The kariPIR class addresses these challenges by offering a robust and efficient mechanism to handle PIR sensors, ensuring that motion is detected and responded to with minimal delay. 
    Its design prioritizes non-blocking operations, allowing your main program to continue running smoothly while the sensor data is processed and appropriate actions are triggered.\n
    With kariPIR, developers gain the ability to execute specific actions based on motion detection events through the use of primary and fallback callback functions. 
    This flexibility allows for sophisticated event handling and responsive behavior in your applications. By abstracting the complexities of sensor integration and signal processing, 
    kariPIR simplifies the development process, enabling you to focus on building innovative and responsive systems with confidence.
    `
    return (
    <>
    <section>
        <h1>{emoji.getUnicode("satellite")} kariPIR</h1>
        <h2>Introduction</h2>
        <p className={`text-formatter ${fs}`}>{introduction}</p>
    </section>
    <section>
        <h2>{emoji.getUnicode("wrench")} Key Features</h2>
        <ul className={`${fs}`}>
            <li><strong>Easy Motion Detection </strong>- Triggers a callback when motion is detected.</li>
            <li><strong>Customizable Fallback Action</strong> - Optional fallback callback when no motion is detected.</li>
            <li><strong>Lightweight & Efficient </strong> - Optimized for low-power applications.</li>
            <li><strong>Plug & Play</strong> - Simple interface for rapid integration.</li>
        </ul>
    </section>
    <h2>{emoji.getUnicode("hammer_and_wrench")} API Reference</h2>
    <CodeFormatter codeSnippet={
        `kariPIR(int signal);\nvoid onMeasure(void(*callback)(), void(*fallbackCallback)() = 0); `
    } />
    <section>
        <h2>{emoji.getUnicode("memo")} Class Methods</h2>
        <CodeArticle
        intro="Constructor"
        index="one"
        other={null}
        codeSignature={`kariPIR(int signal);`}
        codeUsage={`kariPIR motionSensor(7);  // Initialize PIR sensor on pin 7`}
        data={[
            "Constructor that initializes a PIR sensor on the specified signal pin."
        ]}
        />
        <CodeArticle
        intro="Detection of Motion"
        index="two"
        other={null}
        codeSignature={`void onMeasure(void(*callback)(), void(*fallbackCallback)() = 0);`}
        codeUsage={usage}
        data={[
            "Continuously monitors the PIR sensor and executes:",
            "callback() when motion is detected.",
            "fallbackCallback() when no motion is detected (optional)."
        ]}
        />
    </section>
    <h2>{emoji.getUnicode("hammer")} Basic Usage Example:</h2>
    <CodeFormatter codeSnippet={codeSnippet}/>
    </>
    )
}

export default KariPIR
